/**
 * @file Fieldset component for Location Region property.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { FieldsetBase } from '../fieldset-base/fieldset-base';
import { FieldsetProvider } from '../fieldset-provider';
import { FormControl } from '@angular/forms';
import { ComponentRegistry } from '../../../core/component-registry';
/**
 * Fieldset component to edit the Region property of the Location entity.
 * @export
 * @class FieldsetRegionComponent
 * @extends {FieldsetBase}
 */
var FieldsetRegionComponent = /** @class */ (function (_super) {
    tslib_1.__extends(FieldsetRegionComponent, _super);
    /**
     * Creates an instance of FieldsetRegionComponent.
     * @param {FieldsetProvider} provider Provides the entity and the binding field.
     */
    function FieldsetRegionComponent(provider) {
        var _this = _super.call(this, provider) || this;
        /**
         * Holds the last region type of the Location.
         * @private
         */
        _this._regionType = 'C';
        /**
         * Holds the last value of the Loaction Region property.
         * @private
         */
        _this.lastRegion = '';
        /**
         * Holds the value of the region type.
         * @private
         */
        _this._value1 = '';
        /**
         * Holds one of the two values of a ZipCode region type 'Z', namely:
         * The until value of the ZipCode range.
         * @private
         */
        _this._value2 = '';
        /**
         * The FormControl binded to the input for the region type.
         * @type {FormControl}
         */
        _this.regionTypeControl = new FormControl();
        /**
         * The FormControl binded to the input for the second until ZipCode value of the ZipCode range.
         * @type {FormControl}
         */
        _this.valueControl = new FormControl();
        /**
         * The FormControl binded to the input for the longitude.
         * The longitude is shown when the region type is Radius 'R'
         * @type {FormControl}
         */
        _this.longitudeControl = new FormControl();
        /**
         * The FormControl binded to the input for the latitude.
         * The latitude is shown when the region type is Radius 'R'
         * @type {FormControl}
         */
        _this.latitudeControl = new FormControl();
        /**
         * The 5 region types one can choose from.
         * The region type Address 'A' is not really a region, it is a waypoint.
         */
        _this.regionTypes = [
            { labelKey: 'APP.FORMS.REGION.CITY', value: 'C' },
            { labelKey: 'APP.FORMS.REGION.PROVINCE', value: 'P' },
            { labelKey: 'APP.FORMS.REGION.ZIPCODE', value: 'Z' },
            { labelKey: 'APP.FORMS.REGION.RADIUS', value: 'R' },
        ];
        return _this;
    }
    Object.defineProperty(FieldsetRegionComponent.prototype, "value1", {
        /**
         * Gets the value of the region type.
         * @type {string}
         */
        get: function () {
            return this._value1;
        },
        /**
         * Sets the value of a region type.
         */
        set: function (value) {
            this._value1 = value;
            var region = '{"' + this._regionType + '":';
            switch (this._regionType) {
                case 'R':
                    var test = Number.parseFloat(value);
                    if (isNaN(test)) {
                        this._value1 = '0';
                        this.provider.value = region + '0}';
                        break;
                    }
                    this.provider.value = region + test + '}';
                    break;
                case 'Z':
                    this.setZipCodeRegion();
                    break;
                default:
                    this.provider.value = region + '"' + (value + '').trim() + '"}';
                    break;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetRegionComponent.prototype, "value2", {
        /**
         * Gets the second until ZipCode value of a ZipCode range
         * @type {string}
         */
        get: function () {
            return this._value2;
        },
        /**
         * Sets the second until ZipCode value of a ZipCode range
         */
        set: function (value) {
            this._value2 = value;
            if (this._regionType === 'Z') {
                this.setZipCodeRegion();
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Register component by the fieldset provider
     * @static
     */
    FieldsetRegionComponent.register = function () {
        ComponentRegistry.register('fieldset', 'region', FieldsetRegionComponent);
    };
    Object.defineProperty(FieldsetRegionComponent.prototype, "regionType", {
        /**
         * Gets the type of the region property.
         * @readonly
         * @type {string}
         */
        get: function () {
            this.synchronizeValues();
            return this._regionType;
        },
        /**
         * Sets the type of the Region property.
         */
        set: function (value) {
            if (this._regionType === value) {
                return;
            }
            this._regionType = value;
            var region = '{"' + value + '":';
            switch (value) {
                case 'R':
                    this.provider.value = region + '0}';
                    break;
                case 'Z':
                    this.provider.value = region + '[]}';
                    break;
                default:
                    this.provider.value = region + '""}';
                    break;
            }
            this.synchronizeValues();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetRegionComponent.prototype, "longitude", {
        /**
         * Gets the value Longitude property of the Location.
         * @type {string}
         */
        get: function () {
            if (!this.provider.entity) {
                return '';
            }
            return this.provider.entity['Longitude'];
        },
        /**
         * Sets the value Longitude property of the Location.
         */
        set: function (value) {
            if (!this.provider.entity) {
                return;
            }
            this.provider.entity['Longitude'] = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FieldsetRegionComponent.prototype, "latitude", {
        /**
         * Gets the value Latitude property of the Location.
         * @type {string}
         */
        get: function () {
            if (!this.provider.entity) {
                return '';
            }
            return this.provider.entity['Latitude'];
        },
        /**
         * Sets the value Latitude property of the Location.
         */
        set: function (value) {
            if (!this.provider.entity) {
                return;
            }
            this.provider.entity['Latitude'] = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Synchronize the region type values with the Region Location property.
     * @private
     */
    FieldsetRegionComponent.prototype.synchronizeValues = function () {
        var region = (this.provider.value + '').trim();
        if (region !== this.lastRegion) {
            this.lastRegion = region;
            if (region === '') {
                this._regionType = 'C';
                this._value1 = '';
                this._value2 = '';
                return;
            }
            this._value2 = '';
            var regionObject = JSON.parse(region);
            this._regionType = Object.keys(regionObject)[0];
            if (this._regionType === 'Z') {
                var values = regionObject['Z'];
                switch (values.length) {
                    case 0:
                        this._value1 = '';
                        return;
                    case 1:
                        this._value1 = values[0];
                        return;
                    default:
                        this._value1 = values[0];
                        this._value2 = values[1];
                        return;
                }
            }
            this._value1 = regionObject[this._regionType];
        }
    };
    /**
     * Sets the Region Location property when the region type 'Z' ZipCode is selected.
     * @private
     */
    FieldsetRegionComponent.prototype.setZipCodeRegion = function () {
        var zipcodes = '';
        var val1 = this._value1.trim();
        if (val1 !== '') {
            zipcodes = ',"' + val1 + '"';
        }
        var val2 = this._value2.trim();
        if (val2 !== '') {
            zipcodes += ',"' + val2 + '"';
        }
        this.provider.value = '{"Z":[' + zipcodes.substring(1) + ']}';
    };
    return FieldsetRegionComponent;
}(FieldsetBase));
export { FieldsetRegionComponent };
